/**
 * Dataset fetchers for the Intelligence dashboards.
 *
 * A dataset is one bounded read (or a couple of parallel reads) that several
 * catalog sources share — "orders in the window", "latest inventory levels",
 * "daily station output". Widgets never fetch on their own: the page works
 * out which datasets the current layout needs, runs each exactly once via
 * React Query, and hands the resulting bag to every source (see
 * `useDashboardData.ts`). That keeps a dashboard with thirty widgets at a
 * handful of requests instead of thirty.
 *
 * Every dataset declares the permission module it belongs to. The catalog
 * filters sources by `canRead(role, module)` so a manager never sees (or
 * requests) a finance tile — the same gate RLS enforces server-side.
 */

import { supabase } from '@/lib/supabase';
import type { Json, SalesChannelKind, SalesOrderStatus, TransactionDirection } from '@/types/database';
import type {
  ApAgingRow,
  ArAgingRow,
  BalanceSheetRow,
  DailyCashFlowRow,
  MonthlyPnl,
} from '@/features/financials/hooks';
import type { WorkMoraleDay, WorkStationDay } from '@/features/workMode/types';
import { addUtcDays } from '@/features/marketing/range';
import type { DashboardWindow } from './range';
import { daysBetweenInclusive } from './range';

// ---------------------------------------------------------------------------
// Payload shapes
// ---------------------------------------------------------------------------

export interface BankAccountLite {
  id: string;
  name: string;
  account_type: string | null;
  current_balance_cents: number;
}

export type OrderChannelKind = SalesChannelKind | 'other';

export interface OrderLine {
  sku: string | null;
  name: string;
  quantity: number;
  unitPriceCents: number;
}

export interface OrderLite {
  id: string;
  soNumber: string;
  day: string;
  orderedAt: string;
  status: SalesOrderStatus;
  totalCents: number;
  channelKind: OrderChannelKind;
  channelName: string;
  customerKey: string | null;
  customerLabel: string | null;
  lines: OrderLine[];
}

export interface OrdersDataset {
  channels: Array<{ id: string; name: string; kind: SalesChannelKind; isActive: boolean }>;
  orders: OrderLite[];
  /** True when any channel is configured (so "0" means quiet, not disconnected). */
  configured: boolean;
}

export interface ReturnLite {
  id: string;
  rma_number: string;
  status: string;
  channel_kind: SalesChannelKind;
  reason_code: string;
  resolution: string;
  refund_cents: number | null;
  restocking_fee_cents: number;
  requested_at: string;
  day: string;
  valueCents: number;
  units: number;
}

export interface InventoryLevelLite {
  item_id: string;
  location_id: string;
  quantity: number;
  item: {
    sku: string;
    name: string;
    base_uom: string;
    kind: string | null;
    reorder_point: number | null;
    reorder_qty: number | null;
    is_active: boolean | null;
  } | null;
  /** Per base unit: latest PO landed cost, else the supplier price. */
  unit_cost_cents: number | null;
  location: { name: string; code: string } | null;
}

export interface PurchaseOrderLite {
  id: string;
  po_number: string;
  status: string;
  ordered_at: string | null;
  expected_at: string | null;
  delivered_at: string | null;
  total_cents: number;
  created_at: string;
  vendorName: string;
}

export interface DefectLite {
  id: string;
  created_at: string;
  day: string;
  severity: string;
  disposition: string;
  status: string;
  description: string;
  material_cost_cents: number;
  labor_cost_cents: number;
  item_id: string | null;
  workstation_id: string | null;
  scrap_tags: string[];
}

export interface WebDailyLite {
  day: string;
  active_users: number;
  new_users: number;
  sessions: number;
  engaged_sessions: number;
  conversions: number;
  user_engagement_seconds: number;
}

export interface WebReferrerLite {
  day: string;
  channel: string;
  sessions: number;
  active_users: number;
}

export interface WebDataset {
  daily: WebDailyLite[];
  referrers: WebReferrerLite[];
}

export interface AdDailyLite {
  day: string;
  platform: string;
  campaign: string;
  impressions: number;
  clicks: number;
  spend_cents: number;
  conversions: number;
  revenue_cents: number;
}

export interface SearchDailyLite {
  day: string;
  clicks: number;
  impressions: number;
  position: number;
}

export interface SocialDailyLite {
  day: string;
  platform: string;
  account_id: string;
  followers: number;
  follower_delta: number;
  reach: number;
  impressions: number;
  engagements: number;
  posts_published: number;
}

export interface ForecastRunLite {
  id: string;
  ran_at: string;
  method: string;
  horizon_days: number;
  metrics: Record<string, unknown>;
}

export interface ForecastRowLite {
  item_id: string;
  for_date: string;
  predicted_qty: number;
  lower_bound: number | null;
  upper_bound: number | null;
}

export interface ForecastRunsDataset {
  run: ForecastRunLite | null;
  rows: ForecastRowLite[];
}

export interface ItemLite {
  id: string;
  sku: string;
  name: string;
  kind: string | null;
  is_active: boolean;
}

export interface ProjectionSalesLite {
  month: string;
  channel: string;
  sku: string | null;
  units: number;
  unit_price_cents: number;
}

export interface ProjectionPnlLite {
  month: string;
  account_id: string;
  account_type: string;
  account_name: string;
  amount_cents: number;
}

export interface ProjectionCashflowLite {
  txn_date: string;
  amount_cents: number;
  direction: TransactionDirection;
  category: string | null;
  description: string;
}

export interface KpiDefinitionLite {
  id: string;
  key: string;
  label: string;
  unit: string;
  target_value: number | null;
  direction: string;
  visible_to_roles: string[];
}

export interface KpiSnapshotLite {
  kpi_definition_id: string;
  as_of: string;
  value: number;
}

export interface TrackedKpiDataset {
  definitions: KpiDefinitionLite[];
  snapshots: KpiSnapshotLite[];
}

export interface TaskLite {
  id: string;
  name: string;
  status: string;
  is_completed: boolean;
  due_date: string | null;
  completed_at: string | null;
  created_at: string;
  project_id: string;
  projectName: string | null;
  parent_task_id: string | null;
}

export interface DatasetMap {
  bank: BankAccountLite[];
  cashflow_daily: DailyCashFlowRow[];
  pnl_monthly: MonthlyPnl[];
  balance_sheet: BalanceSheetRow[];
  ar_aging: ArAgingRow[];
  ap_aging: ApAgingRow[];
  orders: OrdersDataset;
  returns: ReturnLite[];
  inventory: InventoryLevelLite[];
  purchase_orders: PurchaseOrderLite[];
  station_daily: WorkStationDay[];
  workstations: Array<{ id: string; name: string }>;
  defects: DefectLite[];
  morale: WorkMoraleDay[];
  web: WebDataset;
  ads: AdDailyLite[];
  search: SearchDailyLite[];
  social: SocialDailyLite[];
  forecast_runs: ForecastRunsDataset;
  items: ItemLite[];
  projections_sales: ProjectionSalesLite[];
  projections_pnl: ProjectionPnlLite[];
  projections_cashflow: ProjectionCashflowLite[];
  kpi_tracked: TrackedKpiDataset;
  tasks: TaskLite[];
}

export type DatasetId = keyof DatasetMap;

/** Everything a widget can read. Missing keys are still loading or not permitted. */
export type DatasetBag = Partial<DatasetMap>;

export interface DatasetContext {
  window: DashboardWindow;
}

export interface DatasetSpec<K extends DatasetId = DatasetId> {
  id: K;
  /** Permission module the data belongs to (`canRead(role, module)`). */
  module: string;
  /** Snapshot datasets ignore the window; windowed ones refetch when it moves. */
  windowed: boolean;
  staleTimeMs?: number;
  fetch: (ctx: DatasetContext) => Promise<DatasetMap[K]>;
}

const PAGE_SIZE = 1000;
const MAX_ORDER_ROWS = 20_000;

function n(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function nOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = n(value, Number.NaN);
  return Number.isFinite(parsed) ? parsed : null;
}

function throwIf<T extends { error: unknown }>(result: T): T {
  if (result.error) throw result.error;
  return result;
}

// ---------------------------------------------------------------------------
// Finance
// ---------------------------------------------------------------------------

const bank: DatasetSpec<'bank'> = {
  id: 'bank',
  module: 'financials',
  windowed: false,
  fetch: async () => {
    const res = throwIf(
      await supabase.from('bank_accounts').select('id, name, account_type, current_balance_cents').order('name'),
    );
    return (res.data ?? []) as BankAccountLite[];
  },
};

const cashflowDaily: DatasetSpec<'cashflow_daily'> = {
  id: 'cashflow_daily',
  module: 'financials',
  windowed: true,
  fetch: async ({ window }) => {
    const res = throwIf(
      await supabase
        .from('v_daily_cash_flow' as never)
        .select('*')
        .gte('day', window.fetchStart)
        .order('day'),
    );
    return ((res.data ?? []) as unknown as DailyCashFlowRow[]).map((r) => ({
      ...r,
      day: String(r.day).slice(0, 10),
      amount_cents: n(r.amount_cents),
      txn_count: n(r.txn_count),
    }));
  },
};

const pnlMonthly: DatasetSpec<'pnl_monthly'> = {
  id: 'pnl_monthly',
  module: 'financials',
  windowed: false,
  fetch: async () => {
    const res = throwIf(
      await supabase.from('v_monthly_pnl' as never).select('*').order('month', { ascending: true }),
    );
    return ((res.data ?? []) as unknown as MonthlyPnl[]).map((r) => ({
      ...r,
      month: `${String(r.month).slice(0, 7)}-01`,
      amount_cents: n(r.amount_cents),
    }));
  },
};

const balanceSheet: DatasetSpec<'balance_sheet'> = {
  id: 'balance_sheet',
  module: 'financials',
  windowed: false,
  fetch: async () => {
    const res = throwIf(await supabase.from('v_balance_sheet' as never).select('*'));
    return ((res.data ?? []) as unknown as BalanceSheetRow[]).map((r) => ({
      ...r,
      balance_cents: n(r.balance_cents),
    }));
  },
};

const arAging: DatasetSpec<'ar_aging'> = {
  id: 'ar_aging',
  module: 'financials',
  windowed: false,
  fetch: async () => {
    const res = throwIf(
      await supabase.from('v_ar_aging' as never).select('*').order('total_open_cents', { ascending: false }),
    );
    return (res.data ?? []) as unknown as ArAgingRow[];
  },
};

const apAging: DatasetSpec<'ap_aging'> = {
  id: 'ap_aging',
  module: 'financials',
  windowed: false,
  fetch: async () => {
    const res = throwIf(
      await supabase.from('v_ap_aging' as never).select('*').order('total_open_cents', { ascending: false }),
    );
    return (res.data ?? []) as unknown as ApAgingRow[];
  },
};

// ---------------------------------------------------------------------------
// Sales
// ---------------------------------------------------------------------------

type RawOrder = {
  id: string;
  so_number: string;
  sales_channel_id: string | null;
  customer_id: string | null;
  customer_email: string | null;
  status: SalesOrderStatus;
  ordered_at: string;
  total_cents: number;
  metadata: Json | null;
};

function parseLines(metadata: Json | null): OrderLine[] {
  const bag = (metadata as Record<string, unknown> | null) ?? {};
  if (!Array.isArray(bag.line_items)) return [];
  return (bag.line_items as Array<Record<string, unknown>>).map((li) => ({
    name: String(li.name ?? li.title ?? 'Item'),
    sku: typeof li.sku === 'string' && li.sku.trim() ? li.sku.trim() : null,
    quantity: n(li.quantity),
    unitPriceCents: n(li.unit_price_cents),
  }));
}

function parseCustomerName(metadata: Json | null): string | null {
  const bag = (metadata as Record<string, unknown> | null) ?? {};
  const customer = (bag.customer as Record<string, unknown> | null) ?? null;
  if (!customer) return null;
  const name = [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim();
  return name || null;
}

const orders: DatasetSpec<'orders'> = {
  id: 'orders',
  module: 'sales',
  windowed: true,
  fetch: async ({ window }) => {
    const channelRes = throwIf(
      await supabase.from('sales_channels').select('id, name, kind, is_active'),
    );
    const channels = ((channelRes.data ?? []) as Array<{
      id: string;
      name: string;
      kind: SalesChannelKind;
      is_active: boolean;
    }>).map((c) => ({ id: c.id, name: c.name, kind: c.kind, isActive: c.is_active }));
    const channelById = new Map(channels.map((c) => [c.id, c]));

    const rows: RawOrder[] = [];
    let from = 0;
    const endExclusive = addUtcDays(window.currentEnd, 1);
    while (from < MAX_ORDER_ROWS) {
      const res = throwIf(
        await supabase
          .from('sales_orders')
          .select('id, so_number, sales_channel_id, customer_id, customer_email, status, ordered_at, total_cents, metadata')
          .gte('ordered_at', `${window.fetchStart}T00:00:00Z`)
          .lt('ordered_at', `${endExclusive}T00:00:00Z`)
          .order('ordered_at', { ascending: true })
          .range(from, from + PAGE_SIZE - 1),
      );
      const batch = (res.data ?? []) as RawOrder[];
      rows.push(...batch);
      if (batch.length < PAGE_SIZE) break;
      from += PAGE_SIZE;
    }

    const parsed: OrderLite[] = rows
      .filter((r) => r.status !== 'draft')
      .map((r) => {
        const channel = r.sales_channel_id ? channelById.get(r.sales_channel_id) : undefined;
        const email = r.customer_email?.trim().toLowerCase() || null;
        return {
          id: r.id,
          soNumber: r.so_number,
          day: r.ordered_at.slice(0, 10),
          orderedAt: r.ordered_at,
          status: r.status,
          totalCents: n(r.total_cents),
          channelKind: channel?.kind ?? 'other',
          channelName: channel?.name ?? 'Other',
          customerKey: r.customer_id ?? email,
          customerLabel: parseCustomerName(r.metadata) ?? r.customer_email ?? null,
          lines: parseLines(r.metadata),
        };
      });

    return { channels, orders: parsed, configured: channels.some((c) => c.isActive) };
  },
};

const returns: DatasetSpec<'returns'> = {
  id: 'returns',
  module: 'sales',
  windowed: true,
  fetch: async ({ window }) => {
    const res = throwIf(
      await supabase
        .from('returns' as never)
        .select(
          'id, rma_number, status, channel_kind, reason_code, resolution, refund_cents, restocking_fee_cents, requested_at, return_lines!return_lines_return_id_fkey(quantity, unit_price_cents)',
        )
        .gte('requested_at', `${window.fetchStart}T00:00:00Z`)
        .order('requested_at', { ascending: false })
        .limit(2000),
    );
    type Raw = Omit<ReturnLite, 'day' | 'valueCents' | 'units'> & {
      return_lines: Array<{ quantity: number; unit_price_cents: number | null }> | null;
    };
    return ((res.data ?? []) as unknown as Raw[]).map((r) => {
      const lines = r.return_lines ?? [];
      return {
        id: r.id,
        rma_number: r.rma_number,
        status: r.status,
        channel_kind: r.channel_kind,
        reason_code: r.reason_code,
        resolution: r.resolution,
        refund_cents: nOrNull(r.refund_cents),
        restocking_fee_cents: n(r.restocking_fee_cents),
        requested_at: r.requested_at,
        day: String(r.requested_at).slice(0, 10),
        valueCents: lines.reduce((s, l) => s + n(l.unit_price_cents) * n(l.quantity), 0),
        units: lines.reduce((s, l) => s + n(l.quantity), 0),
      };
    });
  },
};

// ---------------------------------------------------------------------------
// Inventory / procurement / catalog
// ---------------------------------------------------------------------------

const inventory: DatasetSpec<'inventory'> = {
  id: 'inventory',
  module: 'inventory',
  windowed: false,
  fetch: async () => {
    const [res, costs] = await Promise.all([
      supabase
        .from('inventory_levels' as never)
        .select(
          'item_id, location_id, quantity, items(sku, name, base_uom, kind, reorder_point, reorder_qty, is_active), locations(name, code)',
        )
        .then(throwIf),
      supabase
        .from('item_current_cost' as never)
        .select('item_id, unit_cost_cents')
        .then(throwIf),
    ]);
    const costBy = new Map(
      ((costs.data ?? []) as unknown as { item_id: string; unit_cost_cents: number | string | null }[]).map(
        (c) => [c.item_id, c.unit_cost_cents == null ? null : n(c.unit_cost_cents)],
      ),
    );
    type Raw = {
      item_id: string;
      location_id: string;
      quantity: number;
      items: InventoryLevelLite['item'];
      locations: InventoryLevelLite['location'];
    };
    return ((res.data ?? []) as unknown as Raw[]).map((r) => ({
      item_id: r.item_id,
      location_id: r.location_id,
      quantity: n(r.quantity),
      item: r.items,
      location: r.locations,
      unit_cost_cents: costBy.get(r.item_id) ?? null,
    }));
  },
};

const purchaseOrders: DatasetSpec<'purchase_orders'> = {
  id: 'purchase_orders',
  module: 'procurement',
  windowed: false,
  fetch: async () => {
    const res = throwIf(
      await supabase
        .from('purchase_orders' as never)
        .select('id, po_number, status, ordered_at, expected_at, delivered_at, total_cents, created_at, vendors:vendor_id(name)')
        .order('created_at', { ascending: false })
        .limit(2000),
    );
    type Raw = Omit<PurchaseOrderLite, 'vendorName'> & { vendors: { name: string } | null };
    return ((res.data ?? []) as unknown as Raw[]).map((r) => ({
      id: r.id,
      po_number: r.po_number,
      status: r.status,
      ordered_at: r.ordered_at,
      expected_at: r.expected_at,
      delivered_at: r.delivered_at,
      total_cents: n(r.total_cents),
      created_at: r.created_at,
      vendorName: r.vendors?.name ?? 'Unknown vendor',
    }));
  },
};

const items: DatasetSpec<'items'> = {
  id: 'items',
  module: 'catalog',
  windowed: false,
  staleTimeMs: 5 * 60_000,
  fetch: async () => {
    const res = throwIf(
      await supabase
        .from('items' as never)
        .select('id, sku, name, kind, is_active')
        .order('sku', { ascending: true })
        .limit(5000),
    );
    return (res.data ?? []) as unknown as ItemLite[];
  },
};

// ---------------------------------------------------------------------------
// Manufacturing
// ---------------------------------------------------------------------------

function windowDays(window: DashboardWindow): number {
  return Math.max(7, daysBetweenInclusive(window.fetchStart, window.currentEnd));
}

const stationDaily: DatasetSpec<'station_daily'> = {
  id: 'station_daily',
  module: 'manufacturing',
  windowed: true,
  fetch: async ({ window }) => {
    const res = throwIf(
      await supabase.rpc('work_station_daily' as never, { p_days: windowDays(window) } as never),
    );
    return ((res.data ?? []) as unknown as WorkStationDay[]).map((r) => ({
      ...r,
      day: String(r.day).slice(0, 10),
      units: n(r.units),
      scrapped: n(r.scrapped),
      workers: n(r.workers),
      avg_cycle_seconds: nOrNull(r.avg_cycle_seconds),
    }));
  },
};

const workstations: DatasetSpec<'workstations'> = {
  id: 'workstations',
  module: 'manufacturing',
  windowed: false,
  staleTimeMs: 5 * 60_000,
  fetch: async () => {
    const res = throwIf(
      await supabase.from('workstations' as never).select('id, name').order('position', { ascending: true }),
    );
    return (res.data ?? []) as unknown as Array<{ id: string; name: string }>;
  },
};

const defects: DatasetSpec<'defects'> = {
  id: 'defects',
  module: 'manufacturing',
  windowed: true,
  fetch: async ({ window }) => {
    const res = throwIf(
      await supabase
        .from('work_defects' as never)
        .select(
          'id, created_at, severity, disposition, status, description, material_cost_cents, labor_cost_cents, item_id, workstation_id, scrap_tags',
        )
        .or(`created_at.gte.${window.fetchStart}T00:00:00Z,status.eq.open`)
        .order('created_at', { ascending: false })
        .limit(2000),
    );
    type Raw = Omit<DefectLite, 'day' | 'material_cost_cents' | 'labor_cost_cents' | 'scrap_tags'> & {
      material_cost_cents: unknown;
      labor_cost_cents: unknown;
      scrap_tags: unknown;
    };
    return ((res.data ?? []) as unknown as Raw[]).map((r) => ({
      ...r,
      day: String(r.created_at).slice(0, 10),
      material_cost_cents: n(r.material_cost_cents),
      labor_cost_cents: n(r.labor_cost_cents),
      scrap_tags: Array.isArray(r.scrap_tags) ? (r.scrap_tags as string[]) : [],
    }));
  },
};

const morale: DatasetSpec<'morale'> = {
  id: 'morale',
  module: 'manufacturing',
  windowed: true,
  fetch: async ({ window }) => {
    const res = throwIf(
      await supabase.rpc('work_morale_trend' as never, { p_days: windowDays(window) } as never),
    );
    return ((res.data ?? []) as unknown as WorkMoraleDay[]).map((r) => ({
      ...r,
      day: String(r.day).slice(0, 10),
      responses: n(r.responses),
      avg_mood: n(r.avg_mood),
      low_count: n(r.low_count),
    }));
  },
};

// ---------------------------------------------------------------------------
// Marketing
// ---------------------------------------------------------------------------

const web: DatasetSpec<'web'> = {
  id: 'web',
  module: 'marketing',
  windowed: true,
  fetch: async ({ window }) => {
    const [daily, referrers] = await Promise.all([
      supabase
        .from('web_sessions_daily')
        .select('day, active_users, new_users, sessions, engaged_sessions, conversions, user_engagement_seconds')
        .gte('day', window.fetchStart)
        .order('day', { ascending: true }),
      supabase
        .from('web_referrers_daily')
        .select('day, channel, active_users, sessions')
        .gte('day', window.fetchStart),
    ]);
    throwIf(daily);
    throwIf(referrers);
    return {
      daily: (daily.data ?? []) as WebDailyLite[],
      referrers: (referrers.data ?? []) as WebReferrerLite[],
    };
  },
};

const ads: DatasetSpec<'ads'> = {
  id: 'ads',
  module: 'marketing',
  windowed: true,
  fetch: async ({ window }) => {
    const [accounts, metrics] = await Promise.all([
      supabase.from('ad_accounts').select('id, provider').eq('is_active', true),
      supabase
        .from('ad_metrics_daily')
        .select('ad_account_id, day, campaign_external_id, campaign_name, impressions, clicks, spend_cents, conversions, revenue_cents')
        .gte('day', window.fetchStart)
        .order('day', { ascending: true }),
    ]);
    throwIf(accounts);
    throwIf(metrics);
    const platformById = new Map(
      ((accounts.data ?? []) as Array<{ id: string; provider: string }>).map((a) => [a.id, a.provider]),
    );
    type Raw = {
      ad_account_id: string;
      day: string;
      campaign_external_id: string;
      campaign_name: string | null;
      impressions: number;
      clicks: number;
      spend_cents: number;
      conversions: number;
      revenue_cents: number;
    };
    return ((metrics.data ?? []) as Raw[])
      .filter((r) => platformById.has(r.ad_account_id))
      .map((r) => ({
        day: r.day,
        platform: platformById.get(r.ad_account_id) ?? 'unknown',
        campaign: r.campaign_name ?? r.campaign_external_id,
        impressions: n(r.impressions),
        clicks: n(r.clicks),
        spend_cents: n(r.spend_cents),
        conversions: n(r.conversions),
        revenue_cents: n(r.revenue_cents),
      }));
  },
};

const search: DatasetSpec<'search'> = {
  id: 'search',
  module: 'marketing',
  windowed: true,
  fetch: async ({ window }) => {
    const res = throwIf(
      await supabase
        .from('search_console_totals_daily')
        .select('day, clicks, impressions, position')
        .gte('day', window.fetchStart)
        .order('day', { ascending: true }),
    );
    return ((res.data ?? []) as SearchDailyLite[]).map((r) => ({
      day: r.day,
      clicks: n(r.clicks),
      impressions: n(r.impressions),
      position: n(r.position),
    }));
  },
};

const social: DatasetSpec<'social'> = {
  id: 'social',
  module: 'marketing',
  windowed: true,
  fetch: async ({ window }) => {
    const [accounts, metrics] = await Promise.all([
      supabase.from('social_accounts').select('id, provider').eq('is_active', true),
      supabase
        .from('social_account_metrics_daily')
        .select('social_account_id, day, followers, follower_delta, reach, impressions, engagements, posts_published')
        .gte('day', window.fetchStart)
        .order('day', { ascending: true }),
    ]);
    throwIf(accounts);
    throwIf(metrics);
    const platformById = new Map(
      ((accounts.data ?? []) as Array<{ id: string; provider: string }>).map((a) => [a.id, a.provider]),
    );
    type Raw = {
      social_account_id: string;
      day: string;
      followers: number;
      follower_delta: number;
      reach: number;
      impressions: number;
      engagements: number;
      posts_published: number;
    };
    return ((metrics.data ?? []) as Raw[])
      .filter((r) => platformById.has(r.social_account_id))
      .map((r) => ({
        day: r.day,
        account_id: r.social_account_id,
        platform: platformById.get(r.social_account_id) ?? 'unknown',
        followers: n(r.followers),
        follower_delta: n(r.follower_delta),
        reach: n(r.reach),
        impressions: n(r.impressions),
        engagements: n(r.engagements),
        posts_published: n(r.posts_published),
      }));
  },
};

// ---------------------------------------------------------------------------
// Forecasting inputs
// ---------------------------------------------------------------------------

const forecastRuns: DatasetSpec<'forecast_runs'> = {
  id: 'forecast_runs',
  module: 'intelligence',
  windowed: false,
  fetch: async () => {
    const runRes = throwIf(
      await supabase
        .from('forecast_runs' as never)
        .select('id, ran_at, method, horizon_days, metrics')
        .order('ran_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
    );
    const run = (runRes.data as unknown as ForecastRunLite | null) ?? null;
    if (!run) return { run: null, rows: [] };
    const rowsRes = throwIf(
      await supabase
        .from('forecasts' as never)
        .select('item_id, for_date, predicted_qty, lower_bound, upper_bound')
        .eq('forecast_run_id', run.id)
        .order('for_date', { ascending: true })
        .limit(20_000),
    );
    return {
      run,
      rows: ((rowsRes.data ?? []) as unknown as ForecastRowLite[]).map((r) => ({
        item_id: r.item_id,
        for_date: String(r.for_date).slice(0, 10),
        predicted_qty: n(r.predicted_qty),
        lower_bound: nOrNull(r.lower_bound),
        upper_bound: nOrNull(r.upper_bound),
      })),
    };
  },
};

const projectionsSales: DatasetSpec<'projections_sales'> = {
  id: 'projections_sales',
  module: 'financials',
  windowed: false,
  fetch: async () => {
    const res = throwIf(
      await supabase
        .from('projections_sales')
        .select('month, channel, sku, units, unit_price_cents')
        .eq('scenario', 'base')
        .order('month'),
    );
    return ((res.data ?? []) as ProjectionSalesLite[]).map((r) => ({
      ...r,
      month: `${String(r.month).slice(0, 7)}-01`,
      units: n(r.units),
      unit_price_cents: n(r.unit_price_cents),
    }));
  },
};

const projectionsPnl: DatasetSpec<'projections_pnl'> = {
  id: 'projections_pnl',
  module: 'financials',
  windowed: false,
  fetch: async () => {
    const [rows, coa] = await Promise.all([
      supabase.from('projections_pnl').select('month, account_id, amount_cents').eq('scenario', 'base').order('month'),
      supabase.from('chart_of_accounts').select('id, name, account_type'),
    ]);
    throwIf(rows);
    throwIf(coa);
    const accounts = new Map(
      ((coa.data ?? []) as Array<{ id: string; name: string; account_type: string }>).map((a) => [a.id, a]),
    );
    return ((rows.data ?? []) as Array<{ month: string; account_id: string; amount_cents: number }>).map((r) => {
      const account = accounts.get(r.account_id);
      return {
        month: `${String(r.month).slice(0, 7)}-01`,
        account_id: r.account_id,
        account_type: account?.account_type ?? 'unknown',
        account_name: account?.name ?? 'Unknown account',
        amount_cents: n(r.amount_cents),
      };
    });
  },
};

const projectionsCashflow: DatasetSpec<'projections_cashflow'> = {
  id: 'projections_cashflow',
  module: 'financials',
  windowed: false,
  fetch: async () => {
    const res = throwIf(
      await supabase
        .from('projections_cashflow')
        .select('txn_date, amount_cents, direction, category, description')
        .eq('scenario', 'base')
        .order('txn_date'),
    );
    return ((res.data ?? []) as ProjectionCashflowLite[]).map((r) => ({
      ...r,
      txn_date: String(r.txn_date).slice(0, 10),
      amount_cents: n(r.amount_cents),
    }));
  },
};

const kpiTracked: DatasetSpec<'kpi_tracked'> = {
  id: 'kpi_tracked',
  module: 'intelligence',
  windowed: false,
  fetch: async () => {
    const [defs, snaps] = await Promise.all([
      supabase
        .from('kpi_definitions' as never)
        .select('id, key, label, unit, target_value, direction, visible_to_roles')
        .order('label', { ascending: true }),
      supabase
        .from('kpi_snapshots' as never)
        .select('kpi_definition_id, as_of, value')
        .order('as_of', { ascending: true })
        .limit(20_000),
    ]);
    throwIf(defs);
    throwIf(snaps);
    return {
      definitions: ((defs.data ?? []) as unknown as KpiDefinitionLite[]).map((d) => ({
        ...d,
        target_value: nOrNull(d.target_value),
        visible_to_roles: Array.isArray(d.visible_to_roles) ? d.visible_to_roles : [],
      })),
      snapshots: ((snaps.data ?? []) as unknown as KpiSnapshotLite[]).map((s) => ({
        kpi_definition_id: s.kpi_definition_id,
        as_of: String(s.as_of).slice(0, 10),
        value: n(s.value),
      })),
    };
  },
};

const tasks: DatasetSpec<'tasks'> = {
  id: 'tasks',
  module: 'projects',
  windowed: false,
  fetch: async () => {
    const res = throwIf(
      await supabase
        .from('tasks')
        .select('id, name, status, is_completed, due_date, completed_at, created_at, project_id, parent_task_id, projects:project_id(name)')
        .order('created_at', { ascending: false })
        .limit(3000),
    );
    type Raw = Omit<TaskLite, 'projectName'> & { projects: { name: string } | null };
    return ((res.data ?? []) as unknown as Raw[]).map((r) => ({
      id: r.id,
      name: r.name,
      status: r.status,
      is_completed: r.is_completed,
      due_date: r.due_date,
      completed_at: r.completed_at,
      created_at: r.created_at,
      project_id: r.project_id,
      parent_task_id: r.parent_task_id,
      projectName: r.projects?.name ?? null,
    }));
  },
};

export const DATASETS: { [K in DatasetId]: DatasetSpec<K> } = {
  bank,
  cashflow_daily: cashflowDaily,
  pnl_monthly: pnlMonthly,
  balance_sheet: balanceSheet,
  ar_aging: arAging,
  ap_aging: apAging,
  orders,
  returns,
  inventory,
  purchase_orders: purchaseOrders,
  station_daily: stationDaily,
  workstations,
  defects,
  morale,
  web,
  ads,
  search,
  social,
  forecast_runs: forecastRuns,
  items,
  projections_sales: projectionsSales,
  projections_pnl: projectionsPnl,
  projections_cashflow: projectionsCashflow,
  kpi_tracked: kpiTracked,
  tasks,
};

export const DATASET_IDS = Object.keys(DATASETS) as DatasetId[];
